import { useState, useEffect, useRef } from 'react';
import { llmService } from '../../services/api';

export default function DashboardChatbot({ summaryRating, hintConceptSummary, reflectionText, errorTopics, studentId, courseId, logEvent }) {
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [openingLoading, setOpeningLoading] = useState(false);
  const sessionIdRef = useRef(crypto.randomUUID());
  const hasLoggedOpen = useRef(false);
  const prevRating = useRef(null);
  const hasLoggedComplete = useRef(false);

  const userMessageCount = messages.filter(m => m.role === 'user').length;
  const isFinished = userMessageCount >= 5 && messages[messages.length - 1]?.role === 'assistant';

  useEffect(() => {
    if (!summaryRating) return;
    if (prevRating.current === summaryRating) return;
    prevRating.current = summaryRating;

    async function getOpening() {
      setOpeningLoading(true);
      setMessages([]);
      if (!hasLoggedOpen.current) {
        logEvent('chat_opened', summaryRating);
        hasLoggedOpen.current = true;
      }
      try {
        const data = await llmService.chat(
          studentId, courseId,
          [],
          reflectionText,
          summaryRating,
          sessionIdRef.current,
          hintConceptSummary
        );
        setMessages([{ role: 'assistant', content: data.reply }]);
      } catch (error) {
        console.error('Opening error:', error);
        const fallback = summaryRating === 'up'
          ? "That's great that it resonates! How do you think you'll approach improving on these areas?"
          : "What feels off about it — is there a concept you think is missing from this summary?";
        setMessages([{ role: 'assistant', content: fallback }]);
      } finally {
        setOpeningLoading(false);
      }
    }

    getOpening();
  }, [summaryRating]);

  useEffect(() => {
    if (isFinished && !hasLoggedComplete.current) {
      hasLoggedComplete.current = true;
      logEvent('chat_completed', `messages:${userMessageCount}`);
    }
  }, [isFinished]);

  async function sendMessage() {
    if (!inputText.trim() || isLoading || openingLoading || !summaryRating) return;
    const turnNumber = userMessageCount + 1;
    const isLastTurn = turnNumber >= 5;
    const messageContent = isLastTurn
      ? `${inputText.trim()}\n\n[SYSTEM: This is the final message. You MUST end the conversation now. Write exactly 1-2 sentences summarizing what the student reflected on. You are FORBIDDEN from using a question mark. Do not ask anything. End with a period.]`
      : inputText.trim();

    const userMessage = { role: 'user', content: inputText.trim() };
    const newMessages = [...messages, { role: 'user', content: messageContent }];
    setMessages(prev => [...prev, userMessage]);
    setInputText('');
    setIsLoading(true);
    logEvent('chat_message_sent', inputText.slice(0, 200));
    try {
      const data = await llmService.chat(
        studentId, courseId,
        newMessages,
        reflectionText,
        summaryRating,
        sessionIdRef.current,
        hintConceptSummary
      );
      setMessages(prev => [...prev, { role: 'assistant', content: data.reply }]);
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, { role: 'assistant', content: "Sorry, something went wrong. Please try again." }]);
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div style={{
      border: '1px solid #e5e7eb',
      borderRadius: '12px',
      overflow: 'hidden',
      background: 'white'
    }}>
      <div style={{
        background: 'white',
        padding: '0.5rem',
        color: 'white',
        fontWeight: '600',
        fontSize: '15px',
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
      }}>
        <div style={{
          width: '48px',
          height: '48px',
          borderRadius: '50%',
          background: '#6d4aff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          fontSize: '24px',
        }}>
          🤖
        </div>
        Let's talk about your learning
      </div>

      <div style={{
        height: '300px',
        overflowY: 'auto',
        padding: '1rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '0.75rem'
      }}>
        {openingLoading ? (
          <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span>Looking at your work</span>
            <span style={{ display: 'flex', gap: '3px' }}>
              <span className="chat-dot" style={{ animationDelay: '0s' }}>•</span>
              <span className="chat-dot" style={{ animationDelay: '0.2s' }}>•</span>
              <span className="chat-dot" style={{ animationDelay: '0.4s' }}>•</span>
            </span>
          </div> 
          </div>
        ) : (
          messages.map((msg, i) => {
            const assistantIndex = messages.slice(0, i + 1).filter(m => m.role === 'assistant').length;
            const isOpening = assistantIndex === 1;
            const isClosing = isFinished && i === messages.length - 1;
            const turnLabel = !isOpening && !isClosing && msg.role === 'assistant'
              ? `${Math.min(assistantIndex, 5)}/5`
              : isClosing && msg.role === 'assistant'
              ? '✓ Done'
              : null;

            return (
              <div key={i} style={{
                display: 'flex',
                justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start'
              }}>
                <div style={{ maxWidth: '80%' }}>
                <div style={{
                      padding: '0.6rem 0.9rem',
                      borderRadius: msg.role === 'user' ? '12px 12px 0 12px' : '12px 12px 12px 0',
                      background: msg.role === 'user' ? '#6d4aff' : '#f3f4f6',
                      color: msg.role === 'user' ? 'white' : '#111827',
                      fontSize: '15px',
                      lineHeight: '1.5'
                    }}
                      dangerouslySetInnerHTML={{__html: msg.content.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')}}
                    />
                  {turnLabel && (
                    <div style={{
                      fontSize: '11px',
                      color: '#9ca3af',
                      textAlign: 'right',
                      marginTop: '2px'
                    }}>
                      {turnLabel}
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
        {!openingLoading && isLoading && (
          <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
            <div style={{
              padding: '0.6rem 0.9rem',
              borderRadius: '12px 12px 12px 0',
              background: '#f3f4f6',
              color: '#6b7280',
              fontSize: '15px'
            }}>
              Thinking...
            </div>
          </div>
        )}
      </div>

      {isFinished ? (
        <div style={{
          padding: '0.75rem 1rem',
          borderTop: '1px solid #e5e7eb',
          background: '#f0fdf4',
          color: 'black',
          fontSize: '14px',
          fontWeight: '600',
          textAlign: 'center'
        }}>
          ✅ Reflection complete — nice work thinking deeply about your learning.
        </div>
      ) : (
        <div style={{
          padding: '0.75rem 1rem',
          borderTop: '1px solid #e5e7eb',
          display: 'flex',
          gap: '0.5rem'
        }}>
          <input
            type="text"
            value={inputText}
            onChange={e => setInputText(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && sendMessage()}
            placeholder="Type your response..."
            style={{
              flex: 1,
              padding: '0.5rem 0.75rem',
              borderRadius: '8px',
              border: '1px solid #d1d5db',
              fontSize: '15px',
              outline: 'none'
            }}
          />
          <button
            onClick={sendMessage}
            disabled={!inputText.trim() || isLoading || openingLoading}
            style={{
              padding: '0.5rem 1rem',
              background: !inputText.trim() || isLoading || openingLoading ? '#C0C2CF' : '#6d4aff',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: !inputText.trim() || isLoading || openingLoading ? 'not-allowed' : 'pointer',
              fontSize: '15px',
              fontWeight: '600'
            }}
          >
            Send
          </button>
        </div>
      )}
    </div>
  );
}